import { ConfigurableFocusTrap, ConfigurableFocusTrapFactory } from '@angular/cdk/a11y';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, NgZone, OnInit, Renderer2, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  Event,
  GuardsCheckStart,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';

import { rejectFalsy } from '@improver/core/operators';
import { HelpZendeskWidgetService } from '@improver/infrastructure/help-data-layer';
import { ResponsiveService } from '@improver/infrastructure/layout-data-layer';
import { PageTitleService, RouteHistoryService } from '@improver/infrastructure/navigation-data-layer';
import { ImproverLayoutService } from '@improver/shared-ui/layout';

import { NgProgress, NgProgressModule, NgProgressRef } from 'ngx-progressbar';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { LoadingPlaceholderComponent } from './improver/loading-placeholder/loading-placeholder.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgProgressModule, NgClass, RouterOutlet, LoadingPlaceholderComponent],
})
export class AppComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly focusTrapFactory = inject(ConfigurableFocusTrapFactory);
  private readonly helpZendeskWidgetService = inject(HelpZendeskWidgetService);
  private readonly improverLayoutService = inject(ImproverLayoutService);
  private readonly ngProgress = inject(NgProgress);
  private readonly ngZone = inject(NgZone);
  private readonly pageTitleService = inject(PageTitleService);
  private readonly renderer = inject(Renderer2);
  private readonly responsiveService = inject(ResponsiveService);
  private readonly routeHistoryService = inject(RouteHistoryService);
  private readonly router = inject(Router);

  pageReloading = false;
  private handheldDevice: boolean;
  private ngProgressRef: NgProgressRef;
  private readonly distractionFreeClass: string = 'has-distractionFreeModeEnabled';

  protected readonly isLoaded = signal(false);

  ngOnInit() {
    this.ngProgressRef = this.ngProgress.ref();
    this.routeHistoryService.init();
    this.helpZendeskWidgetService.init();

    this.getIsMobile().subscribe((handheldDevice: boolean) => (this.handheldDevice = handheldDevice));
    this.getRouterEvents().subscribe((event: Event) => this.updatePageLoadingState(event));
    this.getCurrentRoute().subscribe((route: ActivatedRoute) => this.updateLayout(route));
    this.getDistractionFreeMode().subscribe((mode: boolean) => this.toggleDistractionFreeStylingClass(mode));
    this.ngZone.runOutsideAngular(() => this.observeCkEditorDialog());
  }

  private updatePageLoadingState(event: Event) {
    if (event instanceof NavigationStart && this.handheldDevice && event.navigationTrigger === 'popstate') {
      this.pageReloading = true;
    }

    if (event instanceof GuardsCheckStart) {
      this.ngProgressRef.start();
    }

    if (event instanceof NavigationEnd || event instanceof NavigationError) {
      this.ngProgressRef.complete();
      this.isLoaded.set(true);
    }
  }

  private updateLayout({ snapshot: { data } }: ActivatedRoute) {
    if (this.ngProgressRef.isStarted) {
      this.ngProgressRef.complete();
    }

    this.pageReloading = false;
    this.improverLayoutService.setSidebarVisibility(data['sidebarVisible'] ?? true);
    this.improverLayoutService.setNavbarVisibility(data['navbarVisible'] ?? true);
    this.improverLayoutService.setNavbarActionsVisibility(data['navbarActionsVisible'] ?? true);
    this.improverLayoutService.setNoScroll(data['noScroll'] ?? false);
    this.improverLayoutService.setNoLayoutContentScroll(data['noLayoutContentScroll'] ?? false);
    this.setPageTitle(data['title'] ?? null);
  }

  private observeCkEditorDialog() {
    const ckEditorDialogClass = 'ck-dialog-overlay';
    let focusTrap: ConfigurableFocusTrap;

    new MutationObserver((mutations: MutationRecord[]) => {
      mutations.forEach((mutation: MutationRecord) => {
        if (mutation.type !== 'childList') {
          return;
        }

        mutation.addedNodes.forEach((node: HTMLElement) => {
          if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains(ckEditorDialogClass)) {
            focusTrap = this.focusTrapFactory.create(node);
          }
        });

        mutation.removedNodes.forEach((node: HTMLElement) => {
          if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains(ckEditorDialogClass) && focusTrap) {
            focusTrap.destroy();
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  private toggleDistractionFreeStylingClass(distractionFreeMode = false) {
    this.ngZone.runOutsideAngular(() => this.setDistractionFreeMode(distractionFreeMode));
  }

  private setDistractionFreeMode(distractionFreeMode: boolean) {
    const { body }: Document = document;

    distractionFreeMode
      ? this.renderer.addClass(body, this.distractionFreeClass)
      : this.renderer.removeClass(body, this.distractionFreeClass);
  }

  private setPageTitle(title: string) {
    this.pageTitleService.setTitle(title);
  }

  private getRouterEvents(): Observable<Event> {
    return this.router.events.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef));
  }

  private getDistractionFreeMode(): Observable<boolean> {
    return this.improverLayoutService.getDistractionFreeMode().pipe(takeUntilDestroyed(this.destroyRef));
  }

  private getIsMobile(): Observable<boolean> {
    return this.responsiveService.isMobileObservable().pipe(takeUntilDestroyed(this.destroyRef));
  }

  private getCurrentRoute(): Observable<ActivatedRoute> {
    return this.routeHistoryService.getCurrentRoute().pipe(rejectFalsy()).pipe(takeUntilDestroyed(this.destroyRef));
  }
}
